import { lazy } from 'react'

import { ClockBack } from '@factorialco/f0-react/icons/app'
import { LegalEntitiesTabsDocument } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { NavigationRoutesFn } from 'modules/core/lib/react-router'

import RedirectToLegalEntityDetails from 'modules/legal_entities/pages/RedirectToLegalEntityDetails'
import Details from 'modules/settings/components/Payroll/LegalEntity/Details'

const RootPage = lazy(() => import('modules/legal_entities/pages/settings/RootPage'))

const AssignPage = lazy(() => import('modules/legal_entities/pages/settings/AssignPage'))

const ContractsLegalEntityPage = lazy(
  () => import('modules/legal_entities/pages/settings/ContractsLegalEntityPage')
)

const CreateEmployeeGroupModalPage = lazy(
  () => import('modules/legal_entities/pages/settings/CreateEmployeeGroupModalPage')
)

const EditPage = lazy(() => import('modules/legal_entities/pages/settings/EditPage'))

const EmployeeGroupRootPage = lazy(
  () => import('modules/legal_entities/pages/settings/EmployeeGroupRootPage')
)

const EmployeeGroupsRootPage = lazy(
  () => import('modules/legal_entities/pages/settings/EmployeeGroupsRootPage')
)

const TimeConditionsLegalEntityPage = lazy(
  () => import('modules/legal_entities/pages/settings/TimeConditionsLegalEntityPage')
)

const MakeMainPage = lazy(() => import('modules/legal_entities/pages/settings/MakeMainPage'))
const RemovePage = lazy(() => import('modules/legal_entities/pages/settings/RemovePage'))
const settingsRoutes: NavigationRoutesFn = (navigation) => {
  return [
    {
      path: navigation.settings.legalEntitiesId.path,
      element: <RootPage />,
      handle: {
        resource: true,
        meta: ({ params, watchQuery, session }) => {
          const legalEntityId = String(params['id'])
          if (!legalEntityId) return { title: '' }

          return watchQuery({
            query: LegalEntitiesTabsDocument,
            variables: { companyId: session.company.id, legalEntityId },
          }).map(({ data }) => {
            const legalEntityname = data?.companies.legalEntitiesConnection.nodes[0]?.legalName
            const canSeeEmployees = data?.apiCore._policies.companySeeLegalEntityEmployees.can
            const isActivityLogEnabled =
              data?.features?.featuresConnection?.nodes?.find((n) => n.name === 'dev_activity_log')
                ?.enabled ?? false
            const canSeeActivityLog = data?.activityLog?._policies?.seeActivityLog?.can ?? false
            const canSeeCycles =
              data?.companies.featureAvailabilitiesConnection.nodes.find(
                (node) => node.id === 'time_balance_annualisation_export'
              )?.available ?? false

            const tin = data?.companies.legalEntitiesConnection.nodes[0]?.tin || ''

            return {
              title: legalEntityname,
              ancestors: [
                {
                  id: 'company_details',
                  title: i18n.t('company_settings.general.company_details.title'),
                  to: navigation.settings.details.fullPath(),
                },
              ],
              headerComponentProps: {
                title: legalEntityname || '',
                description: tin,
                secondaryActions: [
                  {
                    key: 'view-activity',
                    label: i18n.t('activity_log.title'),
                    icon: ClockBack,
                    hideLabel: true,
                    isVisible: isActivityLogEnabled && canSeeActivityLog,
                  },
                ],
              },
              subNavigation: [
                {
                  title: i18n.t('settings.legal_entities.legal_entity.navigation.details'),
                  href: navigation.settings.legalEntitiesId.details.fullPath({
                    id: legalEntityId,
                  }),
                  index: true,
                },
                {
                  title: i18n.t('settings.legal_entities.legal_entity.employee_group.header.title'),
                  href: navigation.settings.legalEntitiesId.employeeGroups.fullPath({
                    id: legalEntityId,
                  }),
                  isVisible: canSeeEmployees,
                },
                {
                  title: i18n.t('settings.legal_entities.legal_entity.navigation.contracts'),
                  href: navigation.settings.legalEntitiesId.contracts.fullPath({
                    id: legalEntityId,
                  }),
                },
                {
                  title: i18n.t('settings.legal_entities.legal_entity.settings.title'),
                  href: navigation.settings.legalEntitiesId.timeConditions.fullPath({
                    id: legalEntityId,
                  }),
                  isVisible: canSeeCycles,
                },
              ],
            }
          })
        },
      },
      children: [
        {
          index: true,
          element: <RedirectToLegalEntityDetails />,
        },
        {
          path: navigation.settings.legalEntitiesId.details.path,
          element: <Details />,
          handle: {
            meta: () => ({
              title: i18n.t('settings.legal_entities.legal_entity.navigation.details'),
            }),
          },
        },
        {
          path: navigation.settings.legalEntitiesId.employeeGroups.path,
          element: <EmployeeGroupsRootPage />,
          handle: {
            meta: () => ({
              illustration: 'network-group',
              title: i18n.t('settings.legal_entities.legal_entity.employee_group.header.title'),
              description: i18n.t(
                'settings.legal_entities.legal_entity.employee_group.header.description'
              ),
            }),
          },
          children: [
            {
              path: navigation.settings.legalEntitiesId.employeeGroups.new.path,
              element: <CreateEmployeeGroupModalPage />,
            },
            {
              path: navigation.settings.legalEntitiesId.employeeGroups.employeeGroupId.path,
              element: <EmployeeGroupRootPage />,
              children: [
                {
                  path: navigation.settings.legalEntitiesId.employeeGroups.employeeGroupId.assign
                    .path,
                  element: <AssignPage />,
                },
                {
                  path: navigation.settings.legalEntitiesId.employeeGroups.employeeGroupId.edit
                    .path,
                  element: <EditPage />,
                },
                {
                  path: navigation.settings.legalEntitiesId.employeeGroups.employeeGroupId.remove
                    .path,
                  element: <RemovePage />,
                },
                {
                  path: navigation.settings.legalEntitiesId.employeeGroups.employeeGroupId.main
                    .path,
                  element: <MakeMainPage />,
                },
              ],
            },
          ],
        },
        {
          path: navigation.settings.legalEntitiesId.contracts.path,
          element: <ContractsLegalEntityPage />,
          handle: {
            meta: () => ({
              title: i18n.t('settings.legal_entities.legal_entity.navigation.contracts'),
            }),
          },
        },
        {
          path: navigation.settings.legalEntitiesId.timeConditions.path,
          element: <TimeConditionsLegalEntityPage />,
          handle: {
            meta: () => ({
              title: i18n.t('settings.legal_entities.legal_entity.navigation.time_conditions'),
            }),
          },
        },
      ],
    },
  ]
}

export default settingsRoutes
